import { type DraggableItemProps } from "@/components/features/matter/file-repo/DraggableItem";
import { FileThumbnail } from "@/components/ui/file-thumbnail";
import { Button, Text } from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { getItemFromPresignedUrlOptions } from "@/lib/data/react-query/s3";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import { getFileDateLabel } from "@/lib/utils/dateUtils";
import {
  getFileIcon,
  getIsFileImage,
  getMatterImageTypeMessage,
  isCriticalImage,
  handleDownload,
  handlePreviewFile,
} from "@/lib/utils/matterFile";
import { useQuery } from "@tanstack/react-query";
import {
  CircleAlert,
  CircleCheck,
  Download,
  Share2,
  SquareArrowOutUpRight,
  Trash2,
} from "lucide-react";
import { type ConnectDragSource } from "react-dnd";

export const FileItem = ({
  item,
  isSelected,
  onSelect,
  onDelete,
  selectedIndex,
  disabled,
  onAddToShareFolder,
  shareFolderLabel,
  drag,
  isDragging,
  dataTestId,
}: DraggableItemProps & {
  drag: ConnectDragSource | null;
  isDragging: boolean;
}) => {
  const { hasActionPermission } = useUser();
  const { data: fileFromPresignedURLData, isLoading: isLoadingFileUrl } =
    useQuery(
      getItemFromPresignedUrlOptions("matter-file", item.presignedUrlKey ?? ""),
    );

  const FileIcon = getFileIcon(item.name);

  const isFileImage = getIsFileImage(item.name);
  const fileUrl = fileFromPresignedURLData?.url ?? null;
  return (
    <div
      ref={disabled ? undefined : drag}
      data-testid={dataTestId}
      className={cn(
        "hover:bg-primary/5 group relative cursor-pointer rounded-md border p-2 transition-colors",
        isSelected && "bg-primary/10 border-primary/30 hover:bg-primary/25",
        isDragging && "opacity-50",
        disabled &&
          "text-muted-foreground border-muted-foreground/50 bg-muted cursor-not-allowed",
      )}
      onClick={disabled ? undefined : onSelect}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="mt-4 flex flex-col items-center p-2">
        <div className="absolute top-0 left-0 flex gap-x-1 p-4">
          {isCriticalImage(item) && (
            <TooltipWrapper message={getMatterImageTypeMessage(item)}>
              <div>
                <Text variant="subHeading" as="span">
                  <CircleAlert className="h-4 w-4" />
                </Text>
              </div>
            </TooltipWrapper>
          )}
          {item.kind === "regular" && shareFolderLabel && (
            <TooltipWrapper message={`Shared in: ${shareFolderLabel}`}>
              <div>
                <Text variant="subHeading" as="span">
                  <Share2 className="h-4 w-4 text-blue-500" />
                </Text>
              </div>
            </TooltipWrapper>
          )}
        </div>
        {isFileImage ? (
          <FileThumbnail
            file={{
              name: item.name,
              type: "",
            }}
            className="mb-2 h-12 w-12 overflow-hidden rounded-sm border-0 shadow-none"
            previewClassName="bg-white"
            previewImageUrl={fileUrl}
            isLoading={isLoadingFileUrl}
            hasError={!fileFromPresignedURLData?.url}
          />
        ) : (
          <FileIcon
            className={cn(
              "mb-2 h-12 w-12 text-blue-500",
              disabled && "text-muted-foreground/50",
            )}
          />
        )}
        <div className="w-full text-center">
          <Text
            variant="subHeading"
            color="foreground"
            as="span"
            className="mx-auto mb-0 block w-full truncate text-center text-nowrap break-normal"
            title={item.name}
          >
            {item.name}
          </Text>
        </div>
        <div className="w-full text-center">
          <Text variant="caption" color="muted" as="p">
            {getFileDateLabel(item.dateCreated, item.dateModified)}
          </Text>
        </div>
      </div>
      {isSelected && (
        <div className="absolute top-0 left-0 flex gap-2 p-4">
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground h-4 w-4"
            type="button"
          >
            {selectedIndex !== undefined && (
              <div className="bg-primary/75 text-primary-foreground flex h-4 w-4 items-center justify-center rounded-full p-2 text-xs">
                {selectedIndex + 1}
              </div>
            )}
            {selectedIndex === undefined && <CircleCheck className="h-4 w-4" />}
          </Button>
        </div>
      )}
      <div
        className={cn(
          "absolute top-0 right-0 flex gap-2 p-4 opacity-0 group-hover:opacity-100",
          disabled && "group-hover:opacity-0",
        )}
      >
        {hasActionPermission(
          ACTION_PERMISSION_KEYS.matterFile,
          ACTION_PERMISSION_VALUES.create,
        ) &&
          onAddToShareFolder && (
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground h-4 w-4"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAddToShareFolder();
              }}
              title="Share this file"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          )}

        <Button
          variant="ghost"
          size="icon"
          className="h-4 w-4 text-blue-500 hover:text-blue-600"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handlePreviewFile(item.name, fileFromPresignedURLData?.url);
          }}
        >
          <SquareArrowOutUpRight className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground h-4 w-4"
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            if (fileFromPresignedURLData?.url) {
              await handleDownload(item.name, fileFromPresignedURLData.url);
            }
          }}
        >
          <Download className="h-4 w-4" />
        </Button>

        {hasActionPermission(
          ACTION_PERMISSION_KEYS.matterFile,
          ACTION_PERMISSION_VALUES.delete,
        ) &&
          onDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-destructive h-4 w-4"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
      </div>
    </div>
  );
};
